/**
 * The cards on screen that a live call can press (Decided S21, amendment
 * A26, and R3's save anyway): one module-wide registry, keyed by the tool call that drew each
 * card, so the call answers the voice's `confirm_card` through the same
 * guarded confirm a tap runs.
 *
 * A card (and a bundle) registers a handle while it is mounted. The call's
 * `confirm_card` reaches `actOnCard`: a card that is not whole on screen is
 * rung and scrolled to, so the person sees which one the voice saves; a card
 * that takes something away raises the touch confirmation, and only a tap
 * acts (`needs_tap`, S20); any other is pressed through the card's own guard,
 * in view or not (R3: the server already heard the save words).
 *
 * It also holds the ring (11.5): which one card is rung for the voice's
 * read-back, and whether the voice has a row of it lit.
 *
 * The call bridge is how a card reports back to the call: a press by hand
 * while the call is up moves the server's ledger, and Change it first on a
 * call has the voice say why the call is ending before the form opens. It is
 * set by the call while its socket is open and cleared when it goes.
 *
 * A plain module with no React Native import, so `bun test` loads it.
 */
import type { AssistantCardAction, AssistantCardResultState } from '@alluzo/shared';

/** What pressing a card did: the state the call is told, and the card's own words for it. */
export type CardResult = { readonly state: AssistantCardResultState; readonly receipt: string | null };

/** The card being read back: ringed, with the row being said lit, or none lit. */
export type ReadBackLook = { readonly row: number | null };

/** What the voice's words are matched against (11.5 fills it; `agent-read-back.ts` imports the type from here). */
export type ReadBackRows = { readonly values: readonly string[]; readonly lights: boolean };

export type CardHandle = {
  readonly isDestructive: boolean;
  /** Offered and ready: a yes could save it. */
  readonly isPending: () => boolean;
  /** What the voice's words are matched against (11.5). */
  readonly rows: () => ReadBackRows;
  /** Ring the card and light a row (11.5), or clear it with null. */
  readonly read: (look: ReadBackLook | null) => void;
  readonly confirm: () => Promise<CardResult>;
  readonly leave: () => CardResult;
  readonly undo: () => Promise<CardResult>;
  readonly isWholeOnScreen: () => Promise<boolean>;
  /** Taller than the docked thread, so never whole on screen. */
  readonly isTallerThanThread: () => Promise<boolean>;
  /** Raise the touch confirmation (11.6). */
  readonly raiseTouch: () => void;
};

/** Every card on screen, in the order it registered. */
const handles = new Map<string, CardHandle>();

/**
 * The cards the call that is up drew, by tool call: what the server's ledger
 * holds as not `earlier` (11.2). A new call starts with none, and a card
 * drawn in an older call or in the typed chat before this one is never in it.
 */
let drawn = new Set<string>();

/** Who hears that a card came or went: the web's attention, which reads the cards in view again. */
const listeners = new Set<() => void>();

/** Hear each card that registers or goes. Answers the way to stop. */
export const onCardsChanged = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const tell = (): void => {
  for (const listener of [...listeners]) listener();
};

/** Register a card's handle. Answers the remover, which removes it only while it is still this handle. */
export const registerCard = (toolCallId: string, handle: CardHandle): (() => void) => {
  // A re-registration moves the card to the end, as a fresh mount would.
  handles.delete(toolCallId);
  handles.set(toolCallId, handle);
  tell();
  return () => {
    if (handles.get(toolCallId) !== handle) return;
    handles.delete(toolCallId);
    tell();
  };
};

export const cardHandleOf = (toolCallId: string): CardHandle | null => handles.get(toolCallId) ?? null;

/**
 * Every registered card a yes could save now, in registration order, each
 * with whether the call that is up drew it (`drawnInCall`): rule 4a counts
 * only those as waiting in this call, and a card drawn before it (A26) is
 * the voice's to mention only when asked.
 */
export const pendingCards = (): readonly { readonly toolCallId: string; readonly handle: CardHandle; readonly drawnInCall: boolean }[] =>
  [...handles.entries()]
    .filter(([, handle]) => handle.isPending())
    .map(([toolCallId, handle]) => ({ toolCallId, handle, drawnInCall: drawn.has(toolCallId) }));

/**
 * The ring (11.5): at most one card is rung at a time, and this holds which,
 * with whether the voice has a row of it lit, and whether the voice has
 * fallen silent since it went on (`held`). A new ring takes the old one off,
 * and the call leaving the dock takes off whichever is on.
 */
let rung: { readonly toolCallId: string; readonly lit: boolean; readonly held: boolean; readonly ring: number } | null = null;

/** How many rings have gone on: each one's number, so the thread tells a new ring from one it left behind. */
let rings = 0;

/**
 * Ring one card, a row of it lit or none, and take the ring off any other.
 * A card still rung from before the voice last fell silent is taken off
 * first too: a card scrolls to itself only as its ring goes on from none, so
 * a read-back that comes back to it (C7b5's pick, the card gone out of view
 * under a later one) brings it into view again. Rows lit while the voice
 * speaks on never scroll again.
 */
export const ringCard = (toolCallId: string, look: ReadBackLook): void => {
  const anew = rung === null || rung.toolCallId !== toolCallId || rung.held;
  if (rung !== null && anew) handles.get(rung.toolCallId)?.read(null);
  if (anew) rings += 1;
  rung = { toolCallId, lit: look.row !== null, held: false, ring: rings };
  handles.get(toolCallId)?.read(look);
};

/**
 * The voice fell silent: the row it lit goes out, and the card keeps its
 * ring, held until the voice rings a card again. Only a lit row is put out,
 * so a card that took its own ring off since the voice lit it (a failed
 * confirm, C68) is never rung again by the voice going quiet after saying
 * what failed.
 */
export const dimRing = (): void => {
  if (rung === null) return;
  const { lit } = rung;
  rung = { ...rung, lit: false, held: true };
  if (lit) handles.get(rung.toolCallId)?.read({ row: null });
};

/**
 * The ring whose card still waits for a yes, rung while the voice reads it
 * back or held since it fell silent, or null: the thread keeps that card
 * where it is, so a reply landing under it (the voice's own words, kept as a
 * message) does not push it out of view, and the person still sees the card
 * they are saying yes to (C60). Each ring that goes on (a card rung from none, another card,
 * or the same one read back again after the voice fell silent) has a key of
 * its own; rows lit as the voice reads on keep it. So once the person starts
 * something new under a ring, the thread follows on past that card, and only
 * a card rung since holds it again (FD14).
 */
export const pendingRing = (): string | null =>
  rung !== null && handles.get(rung.toolCallId)?.isPending() === true ? `${rung.toolCallId}#${rung.ring}` : null;

/** Take the ring off whichever card has it. */
export const clearRing = (): void => {
  if (rung !== null) handles.get(rung.toolCallId)?.read(null);
  rung = null;
};

/**
 * The call's `confirm_card`: no card is `not_found` (the voice says it could
 * not save it); no and undo go to the card as they are; a yes on a card not
 * whole on screen rings it in place of any other ring and scrolls it into
 * view, so the person sees which card the voice acts on (a card taller than
 * the thread to its top; the call's end takes that ring off); then a card
 * that takes something away raises the touch confirmation and answers
 * `needs_tap`, and any other is pressed, in view, out of it, or too tall to
 * be whole (R3: "save anyway"; the server already heard the save words).
 */
export const actOnCard = async ({ toolCallId, action }: { readonly toolCallId: string; readonly action: AssistantCardAction }): Promise<CardResult> => {
  const handle = handles.get(toolCallId);
  if (handle === undefined) return { state: 'not_found', receipt: null };
  if (action === 'leave') return handle.leave();
  if (action === 'undo') return handle.undo();
  if (!(await handle.isWholeOnScreen())) {
    // Through the ring, so the card read back before loses it, and the dock
    // leaving takes this one off too. Taken off first: a card scrolls to
    // itself only as its ring goes on from none, and one the voice look's
    // yes rang keeps that ring into the dock, so a later yes on it, gone out
    // of view again, would never bring it back.
    clearRing();
    ringCard(toolCallId, { row: null });
  }
  if (handle.isDestructive) {
    handle.raiseTouch();
    return { state: 'needs_tap', receipt: null };
  }
  return handle.confirm();
};

/**
 * One run at a time (F2j): while a run is in flight every call answers that
 * same promise, so a finger landing during a voice save — or a second yes —
 * never writes twice; once it settles, rejected or not, the next call runs
 * again. The card's `confirmOnce` and the bundle's run are built on it.
 */
export const guardedOnce = <T>(run: () => Promise<T>): (() => Promise<T>) => {
  let inFlight: Promise<T> | null = null;
  return () => {
    if (inFlight !== null) return inFlight;
    const started = (async () => run())();
    inFlight = started;
    const free = (): void => {
      if (inFlight === started) inFlight = null;
    };
    started.then(free, free);
    return started;
  };
};

/** How a card reaches the call that is up: its result, and the line said before a form opens. */
export type CardCallBridge = {
  readonly report: (toolCallId: string, result: CardResult) => void;
  readonly sayBeforeLeaving: (toolCallId: string, line: string) => Promise<void>;
};

let bridge: CardCallBridge | null = null;

/** The call that is up sets its bridge. Answers the remover, which clears it only while it is still this bridge. */
export const setCardCallBridge = (next: CardCallBridge): (() => void) => {
  bridge = next;
  drawn = new Set();
  return () => {
    if (bridge !== next) return;
    bridge = null;
    drawn = new Set();
  };
};

/** The bridge of the call that is up, or null with no call. */
export const cardCallBridge = (): CardCallBridge | null => bridge;

/** The call that is up drew these cards (its own turn landed with them). Nothing is noted with no call up. */
export const noteDrawnInCall = (toolCallIds: readonly string[]): void => {
  if (bridge === null) return;
  for (const toolCallId of toolCallIds) drawn.add(toolCallId);
};
